<template>
  <v-layout column justify-space-between class="manage-rule white">
    <div>
      <!-- Title and Actions -->
      <v-flex xs12 class="py-0">
        <v-toolbar color="secondary" class="elevation-0">
          <!-- Back Button -->
          <v-btn icon flat class="primary--text text--lighten-5" @click="$router.go(-1)">
            <v-icon>arrow_back</v-icon>
          </v-btn>
          <v-toolbar-title class="white--text subheading ttu">
            <router-link :to="{ name: 'MetadataRulesSearch' }" class="white--text link dim">
              {{ messages.label.METADATA_RULES }}
            </router-link>
            / <span v-if="isEditMode">{{ messages.label.EDIT_RULE }}</span>
            <span v-else>{{ messages.label.CREATE_RULE }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
      </v-flex>
      <!-- Main Content -->
      <v-flex xs12>
        <v-card flat>
          <!-- Header with tabs and actions -->
          <v-card-title class="grey lighten-4 px-3 py-0">
            <v-tabs v-model="activeTab" @change="ensureWindow" color="grey lighten-4">
              <v-tabs-slider color="accent"></v-tabs-slider>
              <v-tab v-for="item in tabItems" :key="item.title" class="secondary--text"
                :class="{ 'd-none': !item.visible }">
                {{ item.title }}
              </v-tab>
              <v-spacer></v-spacer>
            </v-tabs>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="showTestRuleModal()" round v-if="isEditMode">
              {{ messages.label.TEST }}
            </v-btn>
          </v-card-title>
          <!-- Main content of rule (info, conditions, actions) -->
          <v-card-text class="pt-1">
            <!-- Loading indicator -->
            <v-progress-linear color="accent" v-show="dataLoading" height="2" :indeterminate="true">
            </v-progress-linear>
            <!-- Error Alert -->
            <v-flex xs12 v-if="errorAlert">
              <v-alert v-model="errorAlert" dismissible type="error">
                {{ errorMessage }}
              </v-alert>
            </v-flex>
            <v-tabs-items v-model="activeTab" ref="window">
              <v-tab-item>
                <RuleInfo />
              </v-tab-item>
              <v-tab-item :class="{
                'd-none':
                  ruleType !== 'TRANSFORMATION' && ruleType !== 'VALIDATION'
              }">
                <RuleConditions />
              </v-tab-item>
              <v-tab-item :class="{
                'd-none': ruleType !== 'TRANSFORMATION'
              }">
                <RuleActions />
              </v-tab-item>
              <v-tab-item :class="{
                'd-none': ruleType !== 'TRANSFORMATION_JS'
              }">
                <TransformByJSAction />
              </v-tab-item>
              <v-tab-item v-if="isEditMode">
                <JSONView :json="currentRuleJSON" />
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-flex>
      <!-- Rule Test Dialog -->
      <MetadataRuleTest v-model="ruleTestDialog" :rule-obj="ruleObjectToTest" />
      <!-- Snackbar -->
      <v-flex xs12>
        <v-snackbar :bottom="true" :timeout="snackbar.timeout" :color="snackbar.color" v-model="snackbar.show">
          {{ snackbar.message }}
          <v-btn flat color="secondary" @click.native="snackbar.show = false">
            {{ messages.label.OK }}
          </v-btn>
        </v-snackbar>
      </v-flex>
    </div>
    <!-- Card actions -->
    <v-card-actions class="grey lighten-4 pa-3">
      <v-btn color="grey white--text" @click="cancel()">
        {{ messages.label.CANCEL }}
      </v-btn>
      <v-btn color="primary" v-if="isEditMode" :disabled="createOrUpdateInProgress" :loading="createOrUpdateInProgress"
        @click="validateForUpdateRule()">
        {{ messages.label.SAVE }}
      </v-btn>
      <v-btn color="primary" v-else :disabled="createOrUpdateInProgress" :loading="createOrUpdateInProgress"
        @click="validateForCreateRule()">
        {{ messages.label.CREATE }}
      </v-btn>
      <v-spacer>
        <!-- Notification msg for saving rule  -->
        <div class="black--text justify-center layout" v-if="displaySaveRuleMsg">
          <v-icon class="mr-2">warning</v-icon>
          <span class="mt-1">{{ messages.info.SAVE_RULE_MSG }}</span>
        </div>
      </v-spacer>
      <v-btn color="error" v-if="isEditMode" @click="showDeleteDialog()">
        <span>{{ messages.label.DELETE }}</span>
      </v-btn>
      <!-- Delete Dialog -->
      <v-dialog v-model="deleteDialog" width="500">
        <v-card>
          <v-card-title class="subheading ttu">
            {{ messages.label.DELETE }}
          </v-card-title>
          <v-card-text>
            <!-- Error Alert -->
            <v-flex xs12 v-if="deleteErrorAlert">
              <v-alert v-model="deleteErrorAlert" type="error">
                {{ deleteErrorMessage }}
              </v-alert>
            </v-flex>
            <p>
              {{ messages.info.DELETE_RULE_1 }} <b>{{ currentRule.title }}</b> ?
            </p>
            <p v-html="messages.info.DELETE_RULE_2"></p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey lighten-1 white--text" @click="cancelDelete">
              {{ messages.label.CANCEL }}
            </v-btn>
            <v-btn color="error" :disabled="deletingRule" :loading="deletingRule" @click="deleteRule()">
              {{ messages.label.YES }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-actions>
  </v-layout>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex"
import MetadataRulesService from "@/MetadataRules/MetadataRulesService"
import * as metadataRulesDataService from "@/MetadataRules/MetadataRulesDataService"
import * as utils from "@/Shared/utils"
import AppService from "@/AppService"
import RuleConditions from "@/MetadataRules/Rule/RuleConditions"
import RuleActions from "@/MetadataRules/Rule/RuleActions"
import TransformByJSAction from "@/MetadataRules/Rule/TransformByJSAction"
import MetadataRuleTest from "@/MetadataRules/MetadataRuleTest"
import JSONView from "@/MetadataRules/JSONView"
import RuleInfo from "@/MetadataRules/Rule/RuleInfo"
import messages from "@/MetadataRules/messages.json"
import { GET_MPX_PARENT_ACCOUNT } from "@/store/constants/action-types"
import {
  MVATS_WEB_SERVICE_URL,
  RULE_MGMT_DATA_SERVICE_URL
} from "@/store/constants/getter-types"

export default {
  name: "ManageRule",
  components: {
    RuleInfo,
    RuleConditions,
    RuleActions,
    JSONView,
    TransformByJSAction,
    MetadataRuleTest
  },
  data() {
    return {
      activeTab: null,
      isEditMode: false,
      errorAlert: false,
      errorMessage: "",
      ruleObjectToTest: {},
      ruleTestDialog: false,
      validationStatus: {
        info: false,
        conditions: false,
        actions: false,
        jsAction: false
      },
      ruleAction: "",
      ruleInfoData: {},
      ruleConditions: [],
      ruleActions: [],
      actionJS: "",
      ruleType: "",
      currentRule: {},
      currentRuleJSON: {},
      snackbar: {
        show: false,
        timeout: 5000,
        message: "",
        color: "error"
      },
      dataLoading: false,
      createOrUpdateInProgress: false,
      deleteDialog: false,
      deletingRule: false,
      deleteErrorAlert: false,
      deleteErrorMessage: "",
      windowTimeout: null,
      displaySaveRuleMsg: false,
      tabItems: this.getTabItems()
    }
  },
  watch: {
    validationStatus: {
      handler(newValue) {
        if (newValue.info === true && newValue.conditions === true) {
          switch (this.ruleAction) {
            case "create":
              this.createRule()
              break
            case "update":
              this.updateRule()
              break
          }
        }
      },
      deep: true
    }
  },
  computed: {
    ...mapState(["mpxParentAccount"]),
    ...mapGetters([MVATS_WEB_SERVICE_URL, RULE_MGMT_DATA_SERVICE_URL])
  },
  methods: {
    ...mapActions([GET_MPX_PARENT_ACCOUNT]),
    /**
     * validateForUpdateRule - Begin validation for update rule
     */
    validateForUpdateRule() {
      this.ruleAction = "update"
      this.validateAll()
    },

    /**
     * validateForCreateRule - Begin validation for create rule
     */
    validateForCreateRule() {
      this.ruleAction = "create"
      this.validateAll()
    },

    /**
     * resetValidationStatus - Reset validation status
     */
    resetValidationStatus() {
      this.validationStatus = {
        info: false,
        conditions: false
      }
    },

    /**
     * resetData - Reset data
     */
    resetData() {
      if (this.$lodash.isNull(this.activeTab)) {
        this.activeTab = 0
      }
      this.isEditMode = false
      this.ruleAction = ""
      this.ruleInfoData = {}
      this.ruleConditions = []
      this.currentRule = {}
      this.currentRuleJSON = {}
      this.errorAlert = false
      this.errorMessage = ""
      this.displaySaveRuleMsg = false
      this.resetValidationStatus()
    },

    /**
     * validateAll - Validate rule info and conditions
     */
    validateAll() {
      MetadataRulesService.$emit("event:validateRuleInfo")
      MetadataRulesService.$emit("event:validteConditions")
      MetadataRulesService.$emit("event:validteActions")
      MetadataRulesService.$emit("event:validteJSAction")
    },

    /**
     * validateRuleInfo - Handles validation of rule info view
     */
    validateRuleInfo(result, ruleInfoData) {
      this.validationStatus.info = result
      this.ruleInfoData = ruleInfoData
      if (!this.validationStatus.info) {
        this.activeTab = 0 // Show Info tab
      }
    },

    /**
     * validateRuleConditions - Handles validation from rule conditions view
     */
    validateRuleConditions(result, ruleConditions) {
      this.validationStatus.conditions = result
      this.ruleConditions = ruleConditions
      if (!this.validationStatus.conditions) {
        this.activeTab = 1 // Show conditions tab
      }
    },

    /**
     * validateRuleActions - Handles validation from rule actions view
     */
    validateRuleActions(result, ruleActions) {
      this.validationStatus.actions = result
      this.ruleActions = ruleActions
      if (!this.validationStatus.actions) {
        this.activeTab = 2 // Show actions tab
      }
    },

    /**
     * validateRuleJSAction - Handles validation from rule actions view
     */
    validateRuleJSAction(result, actionJS) {
      this.validationStatus.jsAction = result
      this.actionJS = actionJS
      console.log(this.actionJS)
      if (!this.validationStatus.jsAction) {
        this.activeTab = 1 // Show JS actions tab
      }
    },

    /**
     * createRule - Create new rule
     */
    createRule() {
      this.createOrUpdateInProgress = true
      // NOTE: Stringify condition objects before transporting it to back end
      this.ruleConditions = MetadataRulesService.stringifyArrayOfObjects(
        this.ruleConditions
      )
      this.ruleActions = MetadataRulesService.stringifyArrayOfObjects(
        this.ruleActions
      )

      // NOTE: we send account context so we skip 'ownerId' in payload
      let ruleData = {
        title: this.ruleInfoData.title,
        type: this.ruleInfoData.type,
        disabled: !this.ruleInfoData.enabled,
        description: this.ruleInfoData.description,
        secondaryOwner: this.ruleInfoData.secondaryOwner,
        conditions: this.ruleConditions
      }

      if (this.ruleInfoData.type === "TRANSFORMATION_JS") {
        ruleData["actions"] = [this.actionJS]
      } else if (this.ruleInfoData.type === "TRANSFORMATION") {
        ruleData["actions"] = this.ruleActions
      }

      metadataRulesDataService
        .createRule(
          ruleData,
          this.ruleInfoData.ownerId,
          this.RULE_MGMT_DATA_SERVICE_URL
        )
        .then(response => {
          let ruleId = response.data.id.split("/").pop()
          let ownerId = response.data.ownerId
          this.showSnackBar("success", this.messages.info.RULE_CREATE_SUCCESS)
          this.$router.replace({
            name: "ManageRule",
            params: { id: ruleId },
            query: { account: ownerId }
          })
        })
        .catch(error => {
          this.resetValidationStatus()
          this.showError(error)
        })
        .finally(() => {
          this.createOrUpdateInProgress = false
        })
    },

    /**
     * updateRule - Update existing rule
     */
    updateRule() {
      this.createOrUpdateInProgress = true
      // NOTE: Stringify condition objects before transporting it to back end
      this.ruleConditions = MetadataRulesService.stringifyArrayOfObjects(
        this.ruleConditions
      )
      this.ruleActions = MetadataRulesService.stringifyArrayOfObjects(
        this.ruleActions
      )
      // Update current rule data
      // NOTE: we send account context so we skip 'ownerId' in payload
      this.currentRule.title = this.ruleInfoData.title
      this.currentRule.type = this.ruleInfoData.type
      this.currentRule.disabled = !this.ruleInfoData.enabled
      this.currentRule.description = this.ruleInfoData.description
      this.currentRule.secondaryOwner = this.ruleInfoData.secondaryOwner
      this.currentRule.conditions = this.ruleConditions
      this.currentRule.actions = this.ruleActions

      if (this.ruleInfoData.type === "TRANSFORMATION_JS") {
        this.currentRule["actions"] = [this.actionJS]
      } else if (this.ruleInfoData.type === "TRANSFORMATION") {
        this.currentRule["actions"] = this.ruleActions
      }

      return metadataRulesDataService
        .updateRule(
          this.currentRule,
          this.ruleInfoData.ownerId,
          this.RULE_MGMT_DATA_SERVICE_URL
        )
        .then(response => {
          this.showSnackBar("success", this.messages.info.RULE_UPDATE_SUCCESS)
          let ruleId = this.currentRule.id.split("/").pop()
          this.loadRuleData(ruleId, this.currentRule.ownerId)
          return response
        })
        .catch(error => {
          this.showError(error)
        })
        .finally(() => {
          this.resetValidationStatus()
          this.createOrUpdateInProgress = false
        })
    },

    /**
     * retreiveRuleDetails - Retrieve rule details with given id
     * @param {string} ruleId
     * @param {string} account
     */
    retreiveRuleDetails(ruleId, account) {
      this.currentRule = {}
      this.currentRuleJSON = {}
      let queryObject = {
        byId: ruleId,
        account: account
      }
      return metadataRulesDataService
        .getRule(queryObject, this.RULE_MGMT_DATA_SERVICE_URL)
        .then(response => {
          if (response.data.entries.length > 0) {
            this.currentRule = response.data.entries[0]
            this.currentRuleJSON = this.$lodash.cloneDeep(this.currentRule)
            return this.currentRule
          } else {
            // TODO: Redirect to 404
            return Promise.reject(
              new Error(`Could not find rule with id: ${ruleId}`)
            )
          }
        })
        .catch(error => {
          return Promise.reject(error)
        })
    },

    /**
     * loadRuleData - Takes care of loading rule data in the views
     * @param {string} ruleId
     * @param {string} account
     */
    loadRuleData(ruleId, account) {
      this.dataLoading = true
      this.resetData()
      return this.GET_MPX_PARENT_ACCOUNT().then(() => {
        if (!this.$lodash.isUndefined(ruleId)) {
          // Edit mode
          this.isEditMode = true
          return this.retreiveRuleDetails(ruleId, account)
            .then(response => {
              // Rule data retrieved
              this.ruleType = response.type
              this.updateTabsVisibility()
              MetadataRulesService.$emit("event:ruleDataRetrieved", response)
              return response
            })
            .catch(error => {
              this.showError(error)
            })
            .finally(() => {
              this.dataLoading = false
            })
        } else {
          // Create mode
          this.isEditMode = false
          MetadataRulesService.$emit("event:ruleDataRetrieved")
          this.dataLoading = false
          return Promise.resolve({})
        }
      })
    },

    /**
     * showSnackBar - Show snackbar message
     * @param {string} color
     * @param {string} message
     */
    showSnackBar(color, message) {
      this.snackbar.show = true
      this.snackbar.color = color
      this.snackbar.message = message
    },

    /**
     * showError - Handles displaying error
     * @param {object} error
     */
    showError(error) {
      let isServerError = utils.isMPXError(error)
      let isClientError = utils.isClientError(error)
      if (isServerError) {
        this.errorAlert = true
        this.errorMessage = error.response.data.description
      } else if (isClientError) {
        this.errorAlert = true
        this.errorMessage = error.message
      }
    },

    /**
     * cancel - Cancel out of rule create / edit view
     */
    cancel() {
      this.$router.push({ name: "MetadataRulesSearch" })
    },

    /**
     * showDeleteDialog -
     */
    showDeleteDialog() {
      this.deleteDialog = true
    },

    /**
     * cancelDelete - close delete dialog
     */
    cancelDelete() {
      this.deleteErrorAlert = false
      this.deleteErrorMessage = ""
      this.deleteDialog = false
    },

    /**
     * deleteRule - Delete selected rule / ruleset / rule collection
     */
    deleteRule() {
      this.deletingRule = true
      let ruleId = this.currentRule.id.split("/").pop()
      metadataRulesDataService
        .deleteRule(ruleId, this.RULE_MGMT_DATA_SERVICE_URL)
        .then(() => {
          this.deleteDialog = false
          // Show Snackbar
          this.snackbar.show = true
          this.snackbar.color = "success"
          this.snackbar.message =
            this.messages.info.DELETE_SUCCESS + this.currentRule.title
          // route user to search screen
          this.$router.push({ name: "MetadataRulesSearch" })
        })
        .catch(error => {
          let isServerError = utils.isMPXError(error)
          if (isServerError) {
            this.deleteErrorAlert = true
            this.deleteErrorMessage = error.response.data.description
          }
        })
        .finally(() => {
          this.deletingRule = false
        })
    },

    /**
     * showTestRuleModal
     */
    showTestRuleModal() {
      this.ruleObjectToTest = this.currentRule
      this.ruleTestDialog = true
    },

    /**
     * Tab items list
     */
    getTabItems() {
      return [
        { title: "INFO", visible: true },
        { title: "CONDITIONS", visible: false },
        { title: "ACTIONS", visible: false },
        { title: "ACTION", visible: false },
        { title: "{ JSON }", visible: false }
      ]
    },

    /**
     * Update tab visibility
     * NOTE:  We need to do this lengthy logic as dynamic tabs doesn't work correct in current version of Vuetify (1.5)
     */
    updateTabsVisibility() {
      if (this.isEditMode) {
        if (this.ruleType === "TRANSFORMATION") {
          this.tabItems[1].visible = true // Conditions
          this.tabItems[2].visible = true // Actions
          this.tabItems[3].visible = false // Action
          this.tabItems[4].visible = true // JSON
        } else if (this.ruleType === "VALIDATION") {
          this.tabItems[1].visible = true // Conditions
          this.tabItems[2].visible = false // Actions
          this.tabItems[3].visible = false // Action
          this.tabItems[4].visible = true // JSON
        } else if (this.ruleType === "TRANSFORMATION_JS") {
          this.tabItems[1].visible = false // Conditions
          this.tabItems[2].visible = false // Actions
          this.tabItems[3].visible = true // Action
          this.tabItems[4].visible = true // JSON
        }
      } else {
        if (this.ruleType === "VALIDATION") {
          this.tabItems[1].visible = true // Conditions
          this.tabItems[2].visible = false // Actions
          this.tabItems[3].visible = false // Action
          this.tabItems[4].visible = false // JSON
        } else if (this.ruleType === "TRANSFORMATION") {
          this.tabItems[1].visible = true // Conditions
          this.tabItems[2].visible = true // Actions
          this.tabItems[3].visible = false // Action
          this.tabItems[4].visible = false // JSON
        } else if (this.ruleType === "TRANSFORMATION_JS") {
          this.tabItems[1].visible = false // Conditions
          this.tabItems[2].visible = false // Actions
          this.tabItems[3].visible = true // Action
          this.tabItems[4].visible = false // JSON
        }
      }
    },

    /**
     * Handle rule type update
     */
    handleRuleTypeChange(ruleType) {
      this.ruleType = ruleType
      this.updateTabsVisibility()
    },

    /**
     * NOTE: Fix for auto height adjust issue of v-tabs
     * FIXME: Once fix is available, update vuetify version
     */
    ensureWindow() {
      clearTimeout(this.windowTimeout)
      this.windowTimeout = setTimeout(() => {
        this.$refs.window.internalHeight = null
        this.$refs.window.isActive = false
      }, 1000)
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     * */
    MetadataRulesService.$on("event:ruleInfoValidation", this.validateRuleInfo)
    MetadataRulesService.$on(
      "event:ruleConditionsValidation",
      this.validateRuleConditions
    )
    MetadataRulesService.$on(
      "event:ruleActionsValidation",
      this.validateRuleActions
    )
    MetadataRulesService.$on(
      "event:ruleJSActionValidation",
      this.validateRuleJSAction
    )
    MetadataRulesService.$on("event:ruleTypeChange", this.handleRuleTypeChange)
    MetadataRulesService.$on("event:displaySaveRuleMsg", () => {
      this.displaySaveRuleMsg = true
    })
    AppService.$on("event:preIngestServerError", () => {
      this.showSnackBar("error", this.messages.error.SERVER_ERROR)
    })
  },
  /**
   * Triggered on first entry in to the route /metadata-rules/rule
   * @param {object} to
   * @param {object} from
   * @param {Function} next
   */
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.loadRuleData(to.params.id, to.query.account)
    })
  },

  /**
   * Triggered on update to the route /metadata-rules/rule
   * @param {object} to
   * @param {object} from
   * @param {Function} next
   */
  beforeRouteUpdate(to, from, next) {
    this.loadRuleData(to.params.id, to.query.account).then(() => {
      next()
    })
  },
  beforeDestroy() {
    MetadataRulesService.$off("event:ruleInfoValidation", this.validateRuleInfo)
    MetadataRulesService.$off(
      "event:ruleConditionsValidation",
      this.validateRuleConditions
    )
    MetadataRulesService.$off(
      "event:ruleActionsValidation",
      this.validateRuleActions
    )
    MetadataRulesService.$off(
      "event:ruleJSActionValidation",
      this.validateRuleJSAction
    )
    MetadataRulesService.$off("event:ruleTypeChange", this.handleRuleTypeChange)
  }
}
</script>

<style scoped>
.manage-rule .tabs-extension {
  height: 48px;
}

.manage-rule {
  min-height: 90%;
}

.set_padding {
  padding: 5px;
}

.set_margin {
  margin-left: 86px;
  margin-right: 86px;
}
</style>



// WEBPACK FOOTER //
// src/MetadataRules/Rule/ManageRule.vue